<template>
  <v-container>
    <v-snackbar v-model="snackbar" :color="snackColor" :timeout="4000" top>
      <v-row>
        <span>{{ snackText }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click.native="snackbar = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-row>
    </v-snackbar>

    <v-card>
      <v-card-title primary-title class="teal--text">
        <v-icon color="teal" class="mr-3">mdi-cash-refund</v-icon>
        Hotel Refunds
        <v-chip v-if="pendingCount > 0" color="orange" text-color="white" small class="ml-3">
          {{ pendingCount }} pending
        </v-chip>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          dense
          outlined
          class="mt-0"
          style="max-width:260px;"
        />
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="refunds"
          :search="search"
          :loading="loading"
          loading-text="Loading... Please wait"
          :items-per-page="15"
          dense
        >
          <!-- Transfer status chip -->
          <template v-slot:item.is_transferred="{ item }">
            <v-chip small :color="item.is_transferred ? 'green' : 'orange'" text-color="white">
              {{ item.is_transferred ? 'Refunded' : 'Pending' }}
            </v-chip>
          </template>

          <!-- Refund amount highlighted -->
          <template v-slot:item.refund_amount="{ item }">
            <span class="font-weight-bold teal--text">${{ Number(item.refund_amount).toFixed(2) }}</span>
          </template>

          <!-- Total paid -->
          <template v-slot:item.total_paid="{ item }">
            ${{ Number(item.total_paid).toFixed(2) }}
          </template>

          <!-- Date formatted -->
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn text small color="blue" @click="viewRefund(item)">
              <v-icon left small>mdi-eye</v-icon>
              View
            </v-btn>
            <v-btn
              v-if="!item.is_transferred"
              text
              small
              color="teal"
              :loading="processingId === item.id"
              @click="askConfirmRefund(item)"
            >
              <v-icon left small>mdi-bank-transfer-out</v-icon>
              Process Refund
            </v-btn>
            <v-chip v-else x-small color="green lighten-4" text-color="green darken-3" class="ml-1">
              <v-icon x-small left>mdi-check-circle</v-icon>
              Done
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- ─── Refund Detail Dialog ─── -->
    <v-dialog v-model="dialog" max-width="540">
      <v-card v-if="selected">
        <v-card-title class="teal--text">
          <v-icon color="teal" class="mr-2">mdi-cash-refund</v-icon>
          Refund Details
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <!-- Customer info -->
          <div class="overline grey--text mb-2">Customer</div>
          <v-row dense>
            <v-col cols="6">
              <span class="caption grey--text">Name</span>
              <div class="font-weight-medium">{{ selected.customer_name || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <span class="caption grey--text">Phone</span>
              <div class="font-weight-medium">
                <a v-if="selected.customer_phone" :href="`tel:${selected.customer_phone}`" class="teal--text text-decoration-none">
                  {{ selected.customer_phone }}
                </a>
                <span v-else>—</span>
              </div>
            </v-col>
            <v-col cols="12" class="mt-1">
              <span class="caption grey--text">Email</span>
              <div>
                <a v-if="selected.customer_email" :href="`mailto:${selected.customer_email}`" class="teal--text text-decoration-none">
                  {{ selected.customer_email }}
                </a>
                <span v-else>—</span>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-3" />

          <!-- Booking info -->
          <div class="overline grey--text mb-2">Booking</div>
          <v-row dense>
            <v-col cols="12">
              <span class="caption grey--text">Hotel</span>
              <div class="font-weight-medium">{{ selected.hotel_name || '—' }}</div>
            </v-col>
            <v-col cols="6" class="mt-1">
              <span class="caption grey--text">Check-in</span>
              <div>{{ selected.check_in || '—' }}</div>
            </v-col>
            <v-col cols="6" class="mt-1">
              <span class="caption grey--text">Check-out</span>
              <div>{{ selected.check_out || '—' }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-3" />

          <!-- Financial info -->
          <div class="overline grey--text mb-2">Financials</div>
          <v-row dense>
            <v-col cols="6">
              <span class="caption grey--text">Total Charged</span>
              <div class="font-weight-medium">${{ Number(selected.total_paid).toFixed(2) }}</div>
            </v-col>
            <v-col cols="6">
              <span class="caption grey--text">Refund Amount</span>
              <div class="font-weight-bold teal--text text-h6">${{ Number(selected.refund_amount).toFixed(2) }}</div>
              <div class="caption grey--text">(fees excluded)</div>
            </v-col>
          </v-row>

          <v-divider class="my-3" />

          <!-- Status -->
          <v-row dense>
            <v-col cols="6">
              <span class="caption grey--text">Status</span>
              <div class="mt-1">
                <v-chip small :color="selected.is_transferred ? 'green' : 'orange'" text-color="white">
                  {{ selected.is_transferred ? 'Refunded' : 'Pending' }}
                </v-chip>
              </div>
            </v-col>
            <v-col v-if="selected.transferred_at" cols="6">
              <span class="caption grey--text">Refunded At</span>
              <div>{{ formatDate(selected.transferred_at) }}</div>
            </v-col>
            <v-col v-if="selected.payfort_refund_id" cols="12" class="mt-1">
              <span class="caption grey--text">PayFort Refund ID</span>
              <div class="font-weight-medium text-caption">{{ selected.payfort_refund_id }}</div>
            </v-col>
            <v-col cols="6" class="mt-1">
              <span class="caption grey--text">Cancelled On</span>
              <div>{{ formatDate(selected.created_at) }}</div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!selected.is_transferred"
            color="teal"
            dark
            elevation="0"
            :loading="processingId === selected.id"
            @click="askConfirmRefund(selected)"
          >
            <v-icon left>mdi-bank-transfer-out</v-icon>
            Process Refund
          </v-btn>
          <v-btn text @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Confirm Refund Dialog ─── -->
    <v-dialog v-model="confirmDialog" max-width="420" persistent>
      <v-card v-if="confirmTarget" class="pa-2">
        <v-card-title class="teal--text">
          <v-icon color="teal" class="mr-2">mdi-bank-transfer-out</v-icon>
          Confirm Refund
        </v-card-title>
        <v-card-text>
          <p class="body-1 mb-1">
            You are about to refund
            <strong class="teal--text">${{ Number(confirmTarget.refund_amount).toFixed(2) }}</strong>
            to <strong>{{ confirmTarget.customer_name || 'the customer' }}</strong>.
          </p>
          <p class="body-2 grey--text mb-0">
            This will immediately charge PayFort to return the amount to the customer's original card.
            This action <strong>cannot be undone</strong>.
          </p>
          <v-alert v-if="refundError" type="error" dense class="mt-3 mb-0">
            {{ refundError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="processingId !== null" @click="cancelConfirm">
            Cancel
          </v-btn>
          <v-btn
            color="teal"
            dark
            elevation="0"
            :loading="processingId === confirmTarget.id"
            @click="executeRefund"
          >
            <v-icon left>mdi-check</v-icon>
            Confirm Refund
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { headers } from '../../links'

const url = 'https://api.tanefer.com/api/v2'
const adminHotelRefunds = `${url}/admin/hotel-refunds`
const markRefundTransferred = (id) => `${url}/admin/hotel-refunds/${id}/transfer`
const processRefundUrl = (id) => `${url}/admin/hotel-refunds/${id}/process-refund`

export default {
  name: 'HotelRefunds',

  data () {
    return {
      refunds: [],
      loading: false,
      processingId: null,
      dialog: false,
      selected: null,
      confirmDialog: false,
      confirmTarget: null,
      refundError: null,
      search: '',
      snackbar: false,
      snackColor: '',
      snackText: '',
      headers: [
        { text: 'Status', value: 'is_transferred', width: '110px' },
        { text: 'Customer', value: 'customer_name' },
        { text: 'Phone', value: 'customer_phone' },
        { text: 'Hotel', value: 'hotel_name' },
        { text: 'Total Paid', value: 'total_paid' },
        { text: 'Refund Amount', value: 'refund_amount' },
        { text: 'Cancelled On', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },

  computed: {
    pendingCount () {
      return this.refunds.filter(r => !r.is_transferred).length
    }
  },

  created () {
    this.fetchRefunds()
  },

  methods: {
    fetchRefunds () {
      this.loading = true
      this.$http.get(adminHotelRefunds, { headers: headers(this.$cookies.get('userToken')) })
        .then(res => { this.refunds = res.body.refunds || [] })
        .catch(err => { this.showSnack('error', err.body?.message || 'Failed to load refunds') })
        .finally(() => { this.loading = false })
    },

    viewRefund (item) {
      this.selected = item
      this.dialog = true
    },

    askConfirmRefund (item) {
      this.confirmTarget = item
      this.refundError = null
      this.confirmDialog = true
    },

    cancelConfirm () {
      this.confirmDialog = false
      this.confirmTarget = null
      this.refundError = null
    },

    executeRefund () {
      const item = this.confirmTarget
      this.refundError = null
      this.processingId = item.id

      this.$http.post(processRefundUrl(item.id), {}, { headers: headers(this.$cookies.get('userToken')) })
        .then(res => {
          const updated = res.body.refund
          this.updateRefundInList(updated)
          this.confirmDialog = false
          this.confirmTarget = null
          this.showSnack('success', `Refund of $${Number(updated.refund_amount).toFixed(2)} processed successfully`)
        })
        .catch(err => {
          this.refundError = err.body?.message || 'Refund failed. Please try again.'
          this.showSnack('error', this.refundError)
        })
        .finally(() => { this.processingId = null })
    },

    updateRefundInList (updated) {
      const idx = this.refunds.findIndex(r => r.id === updated.id)
      if (idx !== -1) this.$set(this.refunds, idx, updated)
      if (this.selected && this.selected.id === updated.id) this.selected = updated
    },

    formatDate (val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('en-GB', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    },

    showSnack (color, text) {
      this.snackColor = color
      this.snackText = text
      this.snackbar = true
    }
  }
}
</script>
